/** Tipos de dominio compartidos entre api y web. */

export const TIPOS_DOCUMENTO = ['FAC', 'NOTA', 'NC', 'ND', 'COT', 'ENT', 'TRAS', 'CAM', 'ABONO'] as const;
export type TipoDocumento = (typeof TIPOS_DOCUMENTO)[number];

/**
 * Tipos de secuencia en `correlativos`. Además de los documentos, cada sucursal lleva UN consecutivo de N° de
 * control (`CTRL`, 00-NNNNNN) compartido por FAC, NC y ND: es el número preimpreso del papel fiscal (respuesta de la
 * clienta 2026-10-09). CTRL_FAC / CTRL_NC / CTRL_ND son las secuencias anteriores (ya no se usan).
 */
/* 'DESV' = despiece de vidrio (piezas). 'RETC' = retiro parcial de efectivo de una caja. 'OC' = orden de compra (spec 21).
   'PEN' = constancia de pendiente por retirar («Dejar pendiente», Fase 1 de retiros, migración 080): número interno.
   'ORT' = orden de retiro en otra sucursal (Fase 4 de retiros, migración 083): número interno de la sucursal de venta. */
export const TIPOS_CORRELATIVO = [
  ...TIPOS_DOCUMENTO,
  'CTRL',
  'CTRL_FAC',
  'CTRL_NC',
  'CTRL_ND',
  'AJU',
  'CONTEO',
  'COMPRA',
  'RET_IVA',
  'RET_ISLR',
  'GASTO',
  'CIERRE',
  'DESV',
  'RETC',
  'OC',
  'PEN',
  'ORT',
] as const;
export type TipoCorrelativo = (typeof TIPOS_CORRELATIVO)[number];

/** Prefijo del número de control en el papel preimpreso ("00-042580"). */
export const PREFIJO_CONTROL = '00';

/** Tipos que SÍ llevan número de control (los demás no usan papel fiscal). */
export const TIPOS_CON_CONTROL: readonly string[] = ['FAC', 'NC', 'ND'];

/** "A-001523"; sin letra de serie (forma libre de Barquisimeto) → "001523". */
export function formatearNumero(serie: string, numero: number | bigint): string {
  const n = numero.toString().padStart(6, '0');
  return serie ? `${serie}-${n}` : n;
}

/**
 * Número de un documento tal como se guarda, se muestra y se imprime. FAC, NC y ND (los del papel fiscal) van SIN ceros
 * a la izquierda: «28925» en Barquisimeto (sin letra de serie) y «C-33» en Barinas (pedido de la dueña del 2026-10-29).
 * Los demás documentos siguen con 6 dígitos («C-000098»). El número de control no cambia: «00-043086».
 */
export function formatearNumeroDocumento(tipo: string, serie: string, numero: number | bigint): string {
  if (!TIPOS_CON_CONTROL.includes(tipo)) return formatearNumero(serie, numero);
  const n = numero.toString();
  return serie ? `${serie}-${n}` : n;
}

/** "00-104523" */
export function formatearControl(numero: number | bigint): string {
  return `${PREFIJO_CONTROL}-${numero.toString().padStart(6, '0')}`;
}

export const FORMATOS_IMPRESION = ['carta', 'media_carta', 'media_carta_vertical'] as const;
export type FormatoImpresion = (typeof FORMATOS_IMPRESION)[number];

export const TIPOS_ALMACEN = ['tienda', 'deposito'] as const;
export type TipoAlmacen = (typeof TIPOS_ALMACEN)[number];

export const METODOS_PAGO = [
  'efectivo_usd',
  'efectivo_bs',
  'zelle',
  'usdt',
  'punto',
  'transferencia',
  'pago_movil',
  'saldo_favor',
  'cambio',
] as const;
export type MetodoPago = (typeof METODOS_PAGO)[number];

/**
 * Métodos que un usuario puede indicar al cobrar o declarar. "cambio" es INTERNO: el crédito de un CAMBIO que
 * se cruza con su nota de despacho (cambio directo); nunca se acepta desde la pantalla ni el API de cobro.
 */
export const METODOS_PAGO_ENTRADA = [
  'efectivo_usd',
  'efectivo_bs',
  'zelle',
  'usdt',
  'punto',
  'transferencia',
  'pago_movil',
  'saldo_favor',
] as const satisfies readonly MetodoPago[];
export type MetodoPagoEntrada = (typeof METODOS_PAGO_ENTRADA)[number];

/** Métodos cuyo total el cajero siempre declara al cerrar (arqueo por método). */
export const METODOS_ARQUEO = ['efectivo_usd', 'efectivo_bs', 'zelle', 'usdt', 'punto'] as const satisfies readonly MetodoPago[];
/**
 * Pagos bancarios: se declaran también mientras no haya verificación bancaria automática (config
 * `cierre_declara_bancarios`); con verificación, los confirma el banco y no entran al arqueo.
 */
export const METODOS_ARQUEO_BANCARIOS = ['transferencia', 'pago_movil'] as const satisfies readonly MetodoPago[];

/** Métodos que exigen número de referencia (con mínimo de caracteres configurable). */
export const METODOS_CON_REFERENCIA: ReadonlySet<MetodoPago> = new Set<MetodoPago>(['zelle', 'usdt', 'punto', 'transferencia', 'pago_movil']);

/** Caracteres significativos de una referencia: letras y dígitos (se ignoran espacios y guiones). */
export function largoReferencia(referencia: string | null | undefined): number {
  return (referencia ?? '').replace(/[^0-9a-z]/gi, '').length;
}

export const METODO_PAGO_MONEDA: Record<MetodoPago, 'USD' | 'BS'> = {
  efectivo_usd: 'USD',
  efectivo_bs: 'BS',
  zelle: 'USD',
  usdt: 'USD',
  punto: 'BS',
  transferencia: 'BS',
  pago_movil: 'BS',
  saldo_favor: 'USD',
  cambio: 'USD',
};

/**
 * Métodos que se pagan en DIVISA: $ físico, Zelle y USDT. Valen 1:1 con los $ reales del documento y son los que
 * causan IGTF. `saldo_favor` también vive en $ reales (regla de precios 2026-10-09).
 */
export const METODOS_DIVISA: ReadonlySet<MetodoPago> = new Set<MetodoPago>(['efectivo_usd', 'zelle', 'usdt']);

export const METODO_PAGO_ETIQUETA: Record<MetodoPago, string> = {
  efectivo_usd: 'Efectivo $',
  efectivo_bs: 'Efectivo Bs',
  zelle: 'Zelle',
  usdt: 'USDT',
  punto: 'Punto de venta',
  transferencia: 'Transferencia',
  pago_movil: 'Pago móvil',
  saldo_favor: 'Saldo a favor',
  cambio: 'Crédito de cambio',
};

export interface SucursalDTO {
  id: string;
  codigo: string;
  nombre: string;
  /** Letra de la serie fiscal; vacía = forma libre sin letra. */
  serie: string;
  /** Serie del talonario manual (A en Barquisimeto, B en Barinas). */
  serie_manual?: string | null;
  formato_impresion: FormatoImpresion;
  direccion_fiscal: string;
  activa: boolean;
}

export interface AlmacenDTO {
  id: string;
  sucursal_id: string;
  codigo: string;
  nombre: string;
  tipo: TipoAlmacen;
  principal: boolean;
  activo: boolean;
}

export interface CajaDTO {
  id: string;
  sucursal_id: string;
  codigo: string;
  nombre: string;
  activa: boolean;
  /** Fondo fijo de la caja: con él se abre cada turno. */
  fondo_usd?: string;
  fondo_bs?: string;
  /** Banco del punto de venta de la caja. */
  punto_banco?: string | null;
  /** completa = facturas y notas · solo_fiscal = solo facturas. */
  modalidad?: 'completa' | 'solo_fiscal';
}

/**
 * Límite de efectivo de la caja. No lleva montos (conteo a ciegas): solo si se pasó, cuántos avisos van y si la caja
 * quedó bloqueada hasta que se haga el retiro parcial.
 */
export interface EstadoRetiroDTO {
  excede: boolean;
  avisos: number;
  max_avisos: number;
  bloqueada: boolean;
}

export interface SesionCajaDTO {
  id: string;
  caja_id: string;
  caja_nombre: string;
  sucursal_id: string;
  usuario_id: string;
  usuario_nombre: string;
  fondo_usd: string;
  fondo_bs: string;
  abierta_en: string;
  cerrada_en: string | null;
  estado: 'abierta' | 'cerrada';
  /** Turno del día en esa caja (1 = primera sesión del día). */
  turno: number;
  /** El cajero ya declaró su conteo: la caja no vende más y falta terminar el cierre. */
  declarado: boolean;
  /** Al abrir, el fondo encontrado no era el fondo fijo de la caja. */
  fondo_distinto: boolean;
  /** Caja que solo emite facturas (no notas). */
  modalidad: 'completa' | 'solo_fiscal';
  /** Banco del punto de venta de la caja. */
  punto_banco: string | null;
  /** Estado del límite de efectivo (solo en la sesión abierta). */
  retiro: EstadoRetiroDTO | null;
}

/** Respuesta de error estándar de la API. */
export interface ErrorAPI {
  error: { codigo: string; mensaje: string; detalles?: unknown };
}

/** Cómo se entrega el vuelto en bolívares: efectivo (sale de la caja), pago móvil o transferencia (salen del banco). */
export const METODOS_VUELTO_BS = ['efectivo_bs', 'pago_movil', 'transferencia'] as const;
export type MetodoVueltoBs = (typeof METODOS_VUELTO_BS)[number];
export const METODO_VUELTO_BS_ETIQUETA: Record<MetodoVueltoBs, string> = { efectivo_bs: 'Efectivo Bs', pago_movil: 'Pago móvil', transferencia: 'Transferencia' };

/** Mínimo de caracteres de la referencia según el método: el punto lleva los últimos dígitos del comprobante. */
export function minimoReferenciaDe(metodo: MetodoPago, minimos: { punto: number; otros: number }): number {
  return metodo === 'punto' ? minimos.punto : minimos.otros;
}

/**
 * Nombre a la vista de cada tipo de movimiento del kardex (hallazgo G1 del 2026-10-18: se mostraba el nombre interno).
 * El motivo del movimiento dice el resto (una recepción, el faltante que vuelve al origen o una cancelación de traslado).
 */
export const TIPO_MOVIMIENTO_ETIQUETA: Record<string, string> = {
  compra: 'Compra',
  venta_reserva: 'Venta (reserva)',
  entrega: 'Entrega',
  entrega_libera_reserva: 'Entrega (libera la reserva)',
  // «Dejar pendiente» del mismo día: vuelve la existencia al almacén que despachó y queda apartado otra vez (migración 080)
  entrega_reingreso: 'Entrega: queda pendiente',
  // Orden de retiro en otra sucursal (migración 083): lo apartado pasa de una sucursal a otra (solo el por entregar)
  orden_retiro_aparta: 'Orden de retiro: aparta',
  orden_retiro_libera: 'Orden de retiro: libera',
  devolucion: 'Devolución',
  traslado_salida: 'Traslado: sale',
  traslado_entrada: 'Traslado: entra',
  ajuste: 'Ajuste',
  conteo: 'Conteo',
  despiece: 'Despiece',
  despiece_vidrio: 'Corte de vidrio',
  detalle: 'Producto con detalle',
  detalle_reintegro: 'Producto con detalle (vuelve)',
};
